
import Vue from "vue";
import ConfigurationOptions from "@/features/Configuration/ConfigurationOptions";
import { SupplierEventService } from "@/features/SupplierEvents/SupplierEventService";
import { SupplierEventType } from "@/features/SupplierEvents/SupplierEvent";
import SupplierEventProduct from "@/features/SupplierEvents/SupplierEventProduct";
import AppError from "@/shared/components/AppError.vue";

interface FileToUpload {
    name: string;
    progress: number;
}

const SupplierEventProductUpload = Vue.extend({
    name: "SupplierEventProductUpload",
    components: {
        AppError,
    },
    data: () => ({
        dialog: true,
        dragover: false,
        files: [] as FileToUpload[],
        acceptedFileSizeBytes: (Vue.prototype.$configuration as ConfigurationOptions)?.uploads?.maxFileSizeLimitInBytes ?? 15728640,
        acceptedFileTypesMimeTypes: ["text/csv"],
        acceptedFileTypesExtensions: [".csv"],
        uploadInProgress: false,
        errorMessage: "",
        showError: false,
        errors: [] as {
            message: string;
        }[],
        uploadedProducts: [] as SupplierEventProduct[],
        headers: [
            {
                text: "Error",
                value: "message",
                sortable: false,
                align: "start",
            },
        ],
    }),
    props: {
        supplierID: {
            type: String,
            required: true,
        },
        supplierEventType: {
            type: Number as () => SupplierEventType,
            required: true,
        },
    },
    methods: {
        onFileSelected(file: File) {
            if (file) {
                // Called when we select files from the file picker
                console.log("File selected from picker");
                this.uploadFile(file);
            }
        },
        async uploadFile(file: File) {
            this.showError = false;

            // Validate max file size, and file type
            if (this.uploadInProgress) {
                this.errorMessage = `Only one file can be uploaded at a time. Please wait until others have finished.`;
                this.showError = true;
                return;
            }
            if (file.size > this.acceptedFileSizeBytes) {
                this.errorMessage = "File is too large.";
                this.showError = true;
                return;
            }
            if (file.type != undefined && (file.type == "" || !this.acceptedFileTypesMimeTypes.find(t => t.localeCompare(file.type) === 0))) {
                this.errorMessage = "Allowed file types are " + this.acceptedFileTypesExtensions.join(", ");
                this.showError = true;
                return;
            }

            const fileInProgress: FileToUpload = {
                name: file.name,
                progress: 0,
            };
            this.files.push(fileInProgress);

            try {
                this.uploadInProgress = true;
                const supplierEventService = Vue.prototype.$supplierEventService as SupplierEventService;
                const response = await supplierEventService.uploadDocumentForSupplierEventProductProcessing(file, this.supplierID, this.supplierEventType, progress => {
                    fileInProgress.progress = progress * 100;
                });

                if (response.errors.length > 0) {
                    // Table can't handle just strings; needs to be whole objects instead
                    this.errors = response.errors.map(err => ({ message: err }));
                }

                this.uploadedProducts = response.uploadedProducts;
            } catch (e) {
                this.errorMessage = `Failed to upload document ${file.name}`;
                this.showError = true;
            } finally {
                this.uploadInProgress = false;
                this.files = [];
            }
        },
        isPriceEvent() {
            return this.supplierEventType == SupplierEventType.PriceEvent;
        },
        closeModal() {
            this.$emit("modal-closed");
        },
        saveProducts() {
            this.$emit("products-uploaded", this.uploadedProducts);
        },
    },
});

export default SupplierEventProductUpload;
